import { create } from 'zustand';
import {
  CreateVenueBulkResult,
  SimpleVenueData,
  VenueData,
  VenueFilterData,
  VenueSortData,
} from '../../../shared/dto/EntityRepository';
import { PerPage } from '../../../shared/enum/EnumRepository';
import type { ResourceStatus } from '../../../shared/util/scopedResource';
import {
  getDefaultVenueFilterData,
  getDefaultVenueSortData,
} from '../../component/venues/buildVenueListParams';

interface AdminVenueState {
  venueList: SimpleVenueData[];
  activeVenue: VenueData | SimpleVenueData | null;
  multiSelectedVenueIds: string[];
  multiSelectedVenues: VenueData[];
  multiVenueStatus: ResourceStatus;
  bulkResult: CreateVenueBulkResult | null;
  isBulkPreviewOpen: boolean;
  hasChanged: boolean;
  page: number;
  totalPages: number;
  pageSize: PerPage;
  filterData: VenueFilterData;
  sortData: VenueSortData;
  setVenueList: (venueList: SimpleVenueData[]) => void;
  setActiveVenue: (activeVenue: VenueData | SimpleVenueData | null) => void;
  setMultiVenueSelection: (venueIds: string[], venues?: VenueData[]) => void;
  setMultiSelectedVenues: (venues: VenueData[]) => void;
  setMultiVenueStatus: (status: ResourceStatus) => void;
  clearVenueSelection: () => void;
  setBulkResult: (bulkResult: CreateVenueBulkResult | null) => void;
  setIsBulkPreviewOpen: (open: boolean) => void;
  setHasChanged: (hasChanged: boolean) => void;
  setPagination: (page: number, totalPages: number) => void;
  setPageSize: (pageSize: PerPage) => void;
  setFilterData: (filterData: VenueFilterData) => void;
  setSortData: (sortData: VenueSortData) => void;
}

export const useAdminVenueStore = create<AdminVenueState>((set) => ({
  venueList: [],
  activeVenue: null,
  multiSelectedVenueIds: [],
  multiSelectedVenues: [],
  multiVenueStatus: 'idle',
  bulkResult: null,
  isBulkPreviewOpen: false,
  hasChanged: false,
  page: 1,
  totalPages: 0,
  pageSize: PerPage.PAGE_20,
  filterData: getDefaultVenueFilterData(),
  sortData: getDefaultVenueSortData(),
  setVenueList: (venueList) => set({ venueList }),
  setActiveVenue: (activeVenue) => set({
    activeVenue,
    multiSelectedVenueIds: [],
    multiSelectedVenues: [],
    multiVenueStatus: 'idle',
  }),
  setMultiVenueSelection: (venueIds, venues = []) => set({
    activeVenue: null,
    multiSelectedVenueIds: venueIds,
    multiSelectedVenues: venues,
    multiVenueStatus: venues.length === venueIds.length ? 'success' : 'loading',
    hasChanged: false,
  }),
  setMultiSelectedVenues: (multiSelectedVenues) => set({
    multiSelectedVenues,
    multiVenueStatus: 'success',
  }),
  setMultiVenueStatus: (multiVenueStatus) => set({ multiVenueStatus }),
  clearVenueSelection: () => set({
    activeVenue: null,
    multiSelectedVenueIds: [],
    multiSelectedVenues: [],
    multiVenueStatus: 'idle',
    hasChanged: false,
  }),
  setBulkResult: (bulkResult) => set({ bulkResult }),
  setIsBulkPreviewOpen: (isBulkPreviewOpen) => set({ isBulkPreviewOpen }),
  setHasChanged: (hasChanged) => set({ hasChanged }),
  setPagination: (page, totalPages) => set({ page, totalPages }),
  setPageSize: (pageSize) => set({ pageSize }),
  setFilterData: (filterData) => set({ filterData }),
  setSortData: (sortData) => set({ sortData }),
}));
